import { type ReactElement, useState } from 'react';

import {
  Box,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  BadgeCheck,
  CircleX,
  type LucideIcon,
  MoreHorizontal,
  Pencil,
  ShieldCheck,
  Star,
  Trash2,
} from 'lucide-react';

import { CopyableText } from '@modules/_shared/components/CopyableText';
import { useLocale } from '@shared/hooks/locale';

export type EditableContactRowProps = {
  icon: LucideIcon;
  value: string;
  primary?: boolean;
  verified?: boolean;
  display?: ReactElement;
  onEdit: () => void;
  onDelete: () => void;
  onMakePrimary?: () => void;
  onToggleVerified?: () => void;
  isLoading?: boolean;
  verifyPending?: boolean;
  primaryPending?: boolean;
};

export const EditableContactRow = ({
  icon: Icon,
  value,
  primary,
  verified,
  display,
  onEdit,
  onDelete,
  onMakePrimary,
  onToggleVerified,
  isLoading,
  verifyPending,
  primaryPending,
}: EditableContactRowProps) => {
  const { t } = useLocale();
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const closeMenu = (fn: () => void) => () => {
    setMenuAnchor(null);
    fn();
  };

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        minHeight: 28,
        minWidth: 0,
        opacity: isLoading ? 0.5 : 1,
        transition: 'opacity 0.15s ease',
      }}
    >
      <Box
        sx={{
          color: 'text.secondary',
          display: 'flex',
          flexShrink: 0,
          width: 16,
        }}
      >
        <Icon size={16} />
      </Box>

      <CopyableText text={value} sx={{ flex: 1, minWidth: 0 }}>
        {display ?? (
          <Typography
            variant="body2"
            noWrap
            sx={{ minWidth: 0, color: 'text.secondary' }}
          >
            {value}
          </Typography>
        )}
      </CopyableText>

      {primary && (
        <Tooltip title={t('Primary')}>
          <Box
            sx={{
              display: 'flex',
              color: 'primary.main',
              opacity: primaryPending ? 0.5 : 1,
              transition: 'opacity 0.15s ease',
            }}
          >
            <Star size={12} fill="currentColor" strokeWidth={0} />
          </Box>
        </Tooltip>
      )}
      {(verified || verifyPending) && (
        <Tooltip title={t('Verified')}>
          <Box
            sx={{
              display: 'flex',
              color: 'success.main',
              opacity: verifyPending ? 0.5 : 1,
              transition: 'opacity 0.15s ease',
            }}
          >
            <BadgeCheck size={14} />
          </Box>
        </Tooltip>
      )}

      <IconButton
        size="small"
        onClick={evt => setMenuAnchor(evt.currentTarget)}
        sx={{
          width: 24,
          height: 24,
          color: 'text.secondary',
          '&:hover': { backgroundColor: 'action.hover' },
        }}
      >
        <MoreHorizontal size={14} />
      </IconButton>

      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={() => setMenuAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <MenuItem onClick={closeMenu(onEdit)}>
          <ListItemIcon>
            <Pencil size={14} />
          </ListItemIcon>
          <ListItemText>{t('Edit')}</ListItemText>
        </MenuItem>
        {!primary && onMakePrimary && (
          <MenuItem onClick={closeMenu(onMakePrimary)}>
            <ListItemIcon>
              <Star size={14} />
            </ListItemIcon>
            <ListItemText>{t('Mark as primary')}</ListItemText>
          </MenuItem>
        )}
        {onToggleVerified && (
          <MenuItem onClick={closeMenu(onToggleVerified)}>
            <ListItemIcon>
              {verified ? <CircleX size={14} /> : <ShieldCheck size={14} />}
            </ListItemIcon>
            <ListItemText>
              {verified ? t('Mark as unverified') : t('Mark as verified')}
            </ListItemText>
          </MenuItem>
        )}
        <MenuItem onClick={closeMenu(onDelete)} sx={{ color: 'error.main' }}>
          <ListItemIcon sx={{ color: 'error.main' }}>
            <Trash2 size={14} />
          </ListItemIcon>
          <ListItemText>{t('Delete')}</ListItemText>
        </MenuItem>
      </Menu>
    </Box>
  );
};
